import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { packageService } from '@/api/services/packageService'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { toast } from '@/store/useToastStore'
import { cn } from '@/shared/utils'
import { Edit2, ChevronLeft, Infinity as InfinityIcon, CheckSquare, X } from 'lucide-react'
import { PackageCreatePage } from './PackageCreatePage'
import { EditCityInfoModal } from './EditCityInfoModal'
import { Modal } from '@/shared/ui/Modal'
import { Input, Select, Textarea } from '@/shared/ui/Input'
import { Button } from '@/shared/ui/Button'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(v?: string | null) {
  if (!v) return '—'
  try { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' }).replace(/\//g, '-') } catch { return v }
}
function Info({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 gap-4">
      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-xs font-semibold text-gray-900 text-end">{value ?? '—'}</span>
    </div>
  )
}
function YesNo({ v }: { v: boolean }) {
  const { t } = useTranslation()
  return v
    ? <span className="text-xs font-bold text-green-600">{t('packageDetails.yes')}</span>
    : <span className="text-xs font-bold text-gray-400">{t('packageDetails.no')}</span>
}

// Section divider used between the stacked Package Details / City / School blocks
// on the single-page layout (no more tabs).
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{children}</h2>
      <div className="h-px flex-1 bg-gray-200"/>
    </div>
  )
}

// Rebuild the FULL create/update payload from a loaded package detail object,
// exactly mirroring the edit form's payload. Section edits spread a small patch
// on top of this so every other section is resent unchanged.
export function buildPackagePayload(pkg: any): Record<string, any> {
  const mkTr = (loc: string, capLoc: string) => {
    const tr = pkg.package_translations?.find((x:any)=>x.locale?.toLowerCase()===loc)
    const trName = (tr?.name ?? '').trim()
    const incl = (tr?.session_inclusions ?? '').trim()
    return (trName || incl) ? [{ locale: capLoc, name: trName, session_inclusions: incl }] : []
  }
  const translations = [...mkTr('en','En'), ...mkTr('ar','Ar'), ...mkTr('ur','Ur')]

  const isUnlimited = pkg.is_unlimited || pkg.number_of_sessions === -1
  const slotCount = isUnlimited ? 0 : Math.floor(Number(pkg.number_of_sessions || 0) / 2)
  const slotTitles: Record<number, { en: string; ar: string; ur: string }> = {}
  ;(pkg.session_details ?? []).forEach((sd:any) => {
    const idx = (sd.slot_number ?? 0) - 1
    if (idx < 0) return
    const pick = (loc:string) => sd.translations?.find((x:any)=>x.locale?.toLowerCase()===loc)?.title
      ?? sd.translations?.find((x:any)=>x.locale?.toLowerCase()===loc)?.name ?? ''
    slotTitles[idx] = { en: pick('en'), ar: pick('ar'), ur: pick('ur') }
  })
  const slots = Array.from({ length: slotCount }, (_, i) => i)
  const mkSlot = (loc: 'en' | 'ar' | 'ur') => {
    const titles = slots.map(i => (slotTitles[i]?.[loc] ?? '').trim()).filter(Boolean)
    return titles.length ? [{ locale: loc, titles }] : []
  }
  const session_titles = [...mkSlot('en'), ...mkSlot('ar'), ...mkSlot('ur')]

  const citiesArr = (pkg.package_city ?? []).filter((pc:any)=>pc.city_id != null).map((pc:any) => {
    const dType = (pc.discount_type ?? 'percentage').toLowerCase()
    return {
      city_id: pc.city_id,
      ...((pc.id ?? pc.package_city_id) ? { package_city_id: pc.id ?? pc.package_city_id } : {}),
      min_price: Number(pc.min_price || 0),
      max_price: Number(pc.min_price || 0),
      commision: Number(pc.commision || 0),
      commision_type: dType === 'percentage' ? 'percentage' : 'amount',
      discount_type: dType,
      discount_value: Number(pc.discount_value || 0),
      offer_valid_from: pc.offer_valid_from || undefined,
      offer_valid_until: pc.offer_valid_until || undefined,
    }
  })

  const vendorIds = Array.isArray(pkg.package_vendor)
    ? pkg.package_vendor.map((v:any)=>v.vendor_id ?? v.vendor?.id).filter(Boolean)
    : (pkg.vendor_id ? [pkg.vendor_id] : [])

  const trainerChange = pkg.trainer_change_policy_enabled === 1 || !!pkg.is_change_trainer_allowed
  const reschedule = pkg.reschedule_policy_enabled === 1 || !!pkg.is_reschedule_allowed
  const hasDrivysComm = Number(pkg.drivys_commision ?? 0) > 0

  return {
    package_id: pkg.id,
    translations,
    category_id: pkg.category?.id ?? pkg.category_id ?? null,
    vendor_id: vendorIds,
    background_color: pkg.background_color,
    number_of_sessions: isUnlimited ? -1 : Number(pkg.number_of_sessions || 0),
    is_published: pkg.is_published === 1 || pkg.is_published === true ? 1 : 0,
    is_pickup_fee_included: pkg.is_pickup_fee_included ? 1 : 0,
    free_pickup_count: pkg.is_pickup_fee_included ? Number(pkg.free_pickup_count || 0) : 0,
    is_certificate_included: pkg.is_certificate_included ? 1 : 0,
    is_cash_pay_available: pkg.is_cash_pay_available ? 1 : 0,
    is_change_trainer_allowed: trainerChange ? 1 : 0,
    free_km: Number(pkg.free_km || 0),
    drivys_commision: hasDrivysComm ? Number(pkg.drivys_commision || 0) : 0,
    is_drivys_commision_percentage: pkg.is_drivys_commision_percentage ? 1 : 0,
    cities: citiesArr,
    session_titles,
    reschedule_policy: reschedule
      ? { enabled: true, free_reschedule_count: Number(pkg.free_reschedule_count || 0), reschedule_hours_before: Number(pkg.reschedule_hours_before || 30) }
      : { enabled: false, free_reschedule_count: 0, reschedule_hours_before: 30 },
    trainer_change_policy: trainerChange
      ? { enabled: true, free_trainer_change_count: Number(pkg.free_trainer_change_count || 0) }
      : { enabled: false, free_trainer_change_count: 0 },
  }
}

type SaveFn = (patch: Record<string, any>, okKey: string) => Promise<boolean | undefined>

// Small "Edit" pill for a section header (white-on-colour).
function SectionEditButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button onClick={onClick} title={t('packageDetails.edit')}
      className="ms-auto flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-white text-[11px] font-semibold transition-colors">
      <Edit2 size={11}/> {t('packageDetails.edit')}
    </button>
  )
}

function CheckRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
      <span className="text-sm text-gray-700">{label}</span>
      <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} className="w-4 h-4 accent-brand-500"/>
    </label>
  )
}

function EditBasicInfoModal({ open, onClose, pkg, onSave, saving }: { open: boolean; onClose: () => void; pkg: any; onSave: SaveFn; saving: boolean }) {
  const { t } = useTranslation()
  const trName = (loc: string) => pkg.package_translations?.find((x:any)=>x.locale?.toLowerCase()===loc)?.name ?? ''
  const trIncl = (loc: string) => pkg.package_translations?.find((x:any)=>x.locale?.toLowerCase()===loc)?.session_inclusions ?? ''
  const [nameEn, setNameEn] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [nameUr, setNameUr] = useState('')
  const [color, setColor] = useState('unlimited')
  useEffect(() => {
    if (!open) return
    setNameEn(trName('en')); setNameAr(trName('ar')); setNameUr(trName('ur')); setColor(pkg.background_color ?? 'unlimited')
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    const mk = (loc: string, cap: string, name: string) => {
      const nm = name.trim(); const incl = String(trIncl(loc) ?? '').trim()
      return (nm || incl) ? [{ locale: cap, name: nm, session_inclusions: incl }] : []
    }
    const translations = [...mk('en','En',nameEn), ...mk('ar','Ar',nameAr), ...mk('ur','Ur',nameUr)]
    const ok = await onSave({ translations, background_color: color }, 'packageDetails.basicSaved')
    if (ok) onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title={t('packageDetails.editBasic')} size="md">
      <div className="space-y-4">
        <Input label={t('packageDetails.basicInfo.nameEnglish')} value={nameEn} onChange={e=>setNameEn(e.target.value)} />
        <Input label={t('packageDetails.basicInfo.nameArabic')} value={nameAr} onChange={e=>setNameAr(e.target.value)} dir="rtl" />
        <Input label={t('packageDetails.basicInfo.nameUrdu')} value={nameUr} onChange={e=>setNameUr(e.target.value)} dir="rtl" />
        <Select label={t('packageDetails.basicInfo.backgroundColor')} value={color} onChange={e=>setColor(e.target.value)}>
          <option value="unlimited">unlimited</option>
          <option value="gold">gold</option>
          <option value="silver">silver</option>
          <option value="bronze">bronze</option>
          <option value="express">express</option>
        </Select>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('packageDetails.cancel')}</button>
          <Button onClick={submit} loading={saving}>{t('packageDetails.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

function EditPricingModal({ open, onClose, pkg, onSave, saving }: { open: boolean; onClose: () => void; pkg: any; onSave: SaveFn; saving: boolean }) {
  const { t } = useTranslation()
  const [hasComm, setHasComm] = useState(false)
  const [commType, setCommType] = useState('percentage')
  const [commValue, setCommValue] = useState('0')
  useEffect(() => {
    if (!open) return
    setHasComm(Number(pkg.drivys_commision ?? 0) > 0)
    setCommType(pkg.is_drivys_commision_percentage ? 'percentage' : 'amount')
    setCommValue(String(pkg.drivys_commision ?? 0))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    const patch = {
      drivys_commision: hasComm ? Number(commValue || 0) : 0,
      is_drivys_commision_percentage: commType === 'percentage' ? 1 : 0,
    }
    const ok = await onSave(patch, 'packageDetails.pricingSaved')
    if (ok) onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title={t('packageDetails.editPricing')} size="md">
      <div className="space-y-4">
        <p className="text-xs text-gray-400">{t('packageDetails.pricingCityHint')}</p>
        <CheckRow label={t('packageDetails.pricing.hasDrivysCommission')} checked={hasComm} onChange={setHasComm}/>
        {hasComm && (
          <div className="grid grid-cols-2 gap-3">
            <Select label={t('packageDetails.commissionType')} value={commType} onChange={e=>setCommType(e.target.value)}>
              <option value="percentage">{t('packageDetails.percentage')}</option>
              <option value="amount">{t('packageDetails.flatAmount')}</option>
            </Select>
            <Input label={t('packageDetails.pricing.commissionValue')} type="number" value={commValue} onChange={e=>setCommValue(e.target.value)} />
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('packageDetails.cancel')}</button>
          <Button onClick={submit} loading={saving}>{t('packageDetails.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

function EditSettingsModal({ open, onClose, pkg, onSave, saving }: { open: boolean; onClose: () => void; pkg: any; onSave: SaveFn; saving: boolean }) {
  const { t } = useTranslation()
  const [pickup, setPickup] = useState(false)
  const [freePickup, setFreePickup] = useState('0')
  const [freeKm, setFreeKm] = useState('0')
  const [certificate, setCertificate] = useState(false)
  const [cashPay, setCashPay] = useState(false)
  const [trainerChange, setTrainerChange] = useState(false)
  const [freeTrainerChange, setFreeTrainerChange] = useState('0')
  const [reschedule, setReschedule] = useState(false)
  const [freeReschedule, setFreeReschedule] = useState('0')
  const [rescheduleHours, setRescheduleHours] = useState('30')
  useEffect(() => {
    if (!open) return
    setPickup(!!pkg.is_pickup_fee_included)
    setFreePickup(String(pkg.free_pickup_count ?? 0))
    setFreeKm(String(pkg.free_km ?? 0))
    setCertificate(!!pkg.is_certificate_included)
    setCashPay(!!pkg.is_cash_pay_available)
    setTrainerChange(pkg.trainer_change_policy_enabled === 1 || !!pkg.is_change_trainer_allowed)
    setFreeTrainerChange(String(pkg.free_trainer_change_count ?? 0))
    setReschedule(pkg.reschedule_policy_enabled === 1 || !!pkg.is_reschedule_allowed)
    setFreeReschedule(String(pkg.free_reschedule_count ?? 0))
    setRescheduleHours(String(pkg.reschedule_hours_before ?? 30))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    const patch = {
      is_pickup_fee_included: pickup ? 1 : 0,
      free_pickup_count: pickup ? Number(freePickup || 0) : 0,
      free_km: Number(freeKm || 0),
      is_certificate_included: certificate ? 1 : 0,
      is_cash_pay_available: cashPay ? 1 : 0,
      is_change_trainer_allowed: trainerChange ? 1 : 0,
      trainer_change_policy: trainerChange
        ? { enabled: true, free_trainer_change_count: Number(freeTrainerChange || 0) }
        : { enabled: false, free_trainer_change_count: 0 },
      reschedule_policy: reschedule
        ? { enabled: true, free_reschedule_count: Number(freeReschedule || 0), reschedule_hours_before: Number(rescheduleHours || 30) }
        : { enabled: false, free_reschedule_count: 0, reschedule_hours_before: 30 },
    }
    const ok = await onSave(patch, 'packageDetails.settingsSaved')
    if (ok) onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title={t('packageDetails.editSettings')} size="md">
      <div className="space-y-2">
        <CheckRow label={t('packageDetails.settings.pickupAllowed')} checked={pickup} onChange={setPickup}/>
        {pickup && <Input label={t('packageDetails.settings.freePickupCount')} type="number" value={freePickup} onChange={e=>setFreePickup(e.target.value)} />}
        <Input label={t('packageDetails.settings.freeKm')} type="number" value={freeKm} onChange={e=>setFreeKm(e.target.value)} />
        <CheckRow label={t('packageDetails.settings.certificateFeeIncluded')} checked={certificate} onChange={setCertificate}/>
        <CheckRow label={t('packageDetails.settings.payByCash')} checked={cashPay} onChange={setCashPay}/>
        <CheckRow label={t('packageDetails.settings.changeTrainerAllowed')} checked={trainerChange} onChange={setTrainerChange}/>
        {trainerChange && <Input label={t('packageDetails.settings.freeChangeTrainerCount')} type="number" value={freeTrainerChange} onChange={e=>setFreeTrainerChange(e.target.value)} />}
        <CheckRow label={t('packageDetails.settings.rescheduleAllowed')} checked={reschedule} onChange={setReschedule}/>
        {reschedule && (
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('packageDetails.settings.freeRescheduleCount')} type="number" value={freeReschedule} onChange={e=>setFreeReschedule(e.target.value)} />
            <Input label={t('packageDetails.settings.rescheduleMinBefore')} type="number" value={rescheduleHours} onChange={e=>setRescheduleHours(e.target.value)} />
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('packageDetails.cancel')}</button>
          <Button onClick={submit} loading={saving}>{t('packageDetails.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

function EditSessionModal({ open, onClose, pkg, onSave, saving }: { open: boolean; onClose: () => void; pkg: any; onSave: SaveFn; saving: boolean }) {
  const { t } = useTranslation()
  const [unlimited, setUnlimited] = useState(false)
  const [count, setCount] = useState('8')
  const [titles, setTitles] = useState<Record<number, { en: string; ar: string; ur: string }>>({})
  useEffect(() => {
    if (!open) return
    const isUnl = pkg.is_unlimited || pkg.number_of_sessions === -1
    setUnlimited(isUnl)
    setCount(isUnl ? '8' : String(pkg.number_of_sessions ?? 8))
    const out: Record<number, { en: string; ar: string; ur: string }> = {}
    ;(pkg.session_details ?? []).forEach((sd:any) => {
      const idx = (sd.slot_number ?? 0) - 1
      if (idx < 0) return
      const pick = (loc:string) => sd.translations?.find((x:any)=>x.locale?.toLowerCase()===loc)?.title
        ?? sd.translations?.find((x:any)=>x.locale?.toLowerCase()===loc)?.name ?? ''
      out[idx] = { en: pick('en'), ar: pick('ar'), ur: pick('ur') }
    })
    setTitles(out)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const slotCount = unlimited ? 0 : Math.floor(Number(count || 0) / 2)
  const slots = Array.from({ length: slotCount }, (_, i) => i)
  const setTitle = (i: number, loc: 'en'|'ar'|'ur', v: string) =>
    setTitles(p => ({ ...p, [i]: { ...(p[i] ?? { en:'', ar:'', ur:'' }), [loc]: v } }))
  const submit = async () => {
    const mkSlot = (loc: 'en'|'ar'|'ur') => {
      const arr = slots.map(i => (titles[i]?.[loc] ?? '').trim()).filter(Boolean)
      return arr.length ? [{ locale: loc, titles: arr }] : []
    }
    const session_titles = [...mkSlot('en'), ...mkSlot('ar'), ...mkSlot('ur')]
    const patch = { number_of_sessions: unlimited ? -1 : Number(count || 0), session_titles }
    const ok = await onSave(patch, 'packageDetails.sessionSaved')
    if (ok) onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title={t('packageDetails.editSession')} size="lg">
      <div className="space-y-4">
        <CheckRow label={t('packageDetails.session.unlimited')} checked={unlimited} onChange={setUnlimited}/>
        {!unlimited && <Input label={t('packageDetails.session.numberOfSession')} type="number" value={count} onChange={e=>setCount(e.target.value)} />}
        {slots.length > 0 && (
          <div className="space-y-3 max-h-72 overflow-y-auto pe-1">
            {slots.map(i => (
              <div key={i} className="rounded-xl border border-gray-100 p-3 space-y-2">
                <div className="text-xs font-bold text-gray-600">{t('packageDetails.session.sessionName', { index: i+1 })}</div>
                <Input label="EN" value={titles[i]?.en ?? ''} onChange={e=>setTitle(i,'en',e.target.value)} />
                <Input label="AR" value={titles[i]?.ar ?? ''} onChange={e=>setTitle(i,'ar',e.target.value)} dir="rtl" />
                <Input label="UR" value={titles[i]?.ur ?? ''} onChange={e=>setTitle(i,'ur',e.target.value)} dir="rtl" />
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('packageDetails.cancel')}</button>
          <Button onClick={submit} loading={saving}>{t('packageDetails.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Tab: Package Details
// ══════════════════════════════════════════════════════════════════════════════
function DetailsTab({ pkg, onSave, saving, canEdit = true }: { pkg: any; onSave: SaveFn; saving: boolean; canEdit?: boolean }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<null | 'basic' | 'pricing' | 'settings' | 'session'>(null)
  const nameEn = pkg.package_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.name ?? '—'
  const nameAr = pkg.package_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='ar')?.name ?? '—'
  const nameUr = pkg.package_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='ur')?.name ?? '—'
  const vehicleType = pkg.category?.category_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.name ?? '—'
  const bgColor = pkg.background_color ?? '—'
  const isPublished = pkg.is_published === 1

  const minPrice  = Math.round(Number(pkg.min_price ?? 0))
  const maxPrice  = Math.round(Number(pkg.max_price ?? 0))
  const dc0          = pkg.package_city?.[0]
  const discount     = Number(dc0?.discount_value ?? 0)
  const discountType = dc0?.discount_type ?? 'percentage'
  const startDate    = dc0?.offer_valid_from
  const endDate      = dc0?.offer_valid_until
  const hasDrivys = pkg.drivys_commision > 0

  // Session titles
  const sessionDetails = pkg.session_details ?? []
  const sessionSlotCount = Math.floor((pkg.number_of_sessions ?? 0) / 2)
  const isUnlimited = pkg.number_of_sessions === -1 || pkg.is_unlimited

  return (
    <div className="space-y-5">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* Basic Info */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-brand-500 flex items-center gap-2">
          <span className="text-sm font-bold text-white">{t('packageDetails.basicInfo.title')}</span>
          {canEdit && <SectionEditButton onClick={()=>setEditing('basic')}/>}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-base font-bold text-gray-900">{nameEn}</div>
              <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold',
                isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                {isPublished ? t('packageDetails.published') : t('packageDetails.unpublished')}
              </span>
            </div>
          </div>
          <div className="space-y-0">
            <Info label={t('packageDetails.basicInfo.nameArabic')} value={<span dir="rtl">{nameAr}</span>}/>
            <Info label={t('packageDetails.basicInfo.nameUrdu')}   value={<span dir="rtl">{nameUr}</span>}/>
            <Info label={t('packageDetails.basicInfo.vehicleType')}  value={vehicleType}/>
            <Info label={t('packageDetails.basicInfo.backgroundColor')} value={
              <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold capitalize',
                bgColor==='unlimited'?'bg-purple-100 text-purple-700':
                bgColor==='gold'?'bg-brand-100 text-brand-700':
                bgColor==='silver'?'bg-gray-100 text-gray-600':
                bgColor==='bronze'?'bg-orange-100 text-orange-700':
                bgColor==='express'||bgColor==='red'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-600'
              )}>{bgColor}</span>
            }/>
          </div>
        </div>
      </div>

      {/* Drivys Commission (package-level; per-city price/discount live in the Pricing section below) */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-teal-500 flex items-center gap-2">
          <span className="text-sm font-bold text-white">{t('packageDetails.commissionTitle')}</span>
          {canEdit && <SectionEditButton onClick={()=>setEditing('pricing')}/>}
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="text-center p-3 rounded-xl bg-gray-50 border border-gray-100">
            <div className="text-sm font-black text-gray-900"><YesNo v={hasDrivys}/></div>
            <div className="text-[11px] text-gray-400 mt-0.5">{t('packageDetails.pricing.hasDrivysCommission')}</div>
          </div>
          <div className="text-center p-3 rounded-xl bg-gray-50 border border-gray-100">
            <div className="text-sm font-black text-gray-900">{pkg.drivys_commision ?? 0}{pkg.is_drivys_commision_percentage ? '%' : ' AED'}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{t('packageDetails.pricing.commissionValue')}</div>
          </div>
        </div>
      </div>

      {/* Package Settings */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-green-500 flex items-center gap-2">
          <span className="text-sm font-bold text-white">{t('packageDetails.settings.title')}</span>
          {canEdit && <SectionEditButton onClick={()=>setEditing('settings')}/>}
        </div>
        <div className="p-5 space-y-0">
          <Info label={t('packageDetails.settings.pickupAllowed')}         value={<YesNo v={!!pkg.is_pickup_fee_included}/>}/>
          <Info label={t('packageDetails.settings.freePickupCount')}      value={pkg.free_pickup_count ?? 0}/>
          <Info label={t('packageDetails.settings.freeKm')}                value={pkg.free_km != null ? `${Number(pkg.free_km)} KM` : '—'}/>
          <Info label={t('packageDetails.settings.certificateFeeIncluded')} value={<YesNo v={!!pkg.is_certificate_included}/>}/>
          <Info label={t('packageDetails.settings.payByCash')}            value={<YesNo v={!!pkg.is_cash_pay_available}/>}/>
          <Info label={t('packageDetails.settings.changeTrainerAllowed')} value={<YesNo v={!!pkg.trainer_change_policy_enabled}/>}/>
          <Info label={t('packageDetails.settings.freeChangeTrainerCount')} value={pkg.free_trainer_change_count ?? 0}/>
          <Info label={t('packageDetails.settings.rescheduleAllowed')}     value={<YesNo v={!!pkg.reschedule_policy_enabled}/>}/>
          <Info label={t('packageDetails.settings.freeRescheduleCount')}  value={pkg.free_reschedule_count ?? 0}/>
          <Info label={t('packageDetails.settings.rescheduleMinBefore')} value={
            pkg.reschedule_hours_before ? t('packageDetails.settings.minBeforeSuffix', { count: Number(pkg.reschedule_hours_before) }) : '—'
          }/>
        </div>
      </div>

      {/* Session Info */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-teal-500 flex items-center gap-2">
          <span className="text-sm font-bold text-white">{t('packageDetails.session.title')}</span>
          {canEdit && <SectionEditButton onClick={()=>setEditing('session')}/>}
        </div>
        <div className="p-5">
          {/* Session / Number of sessions tiles */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 text-center">
              <div className="text-base font-bold text-gray-700">
                {isUnlimited ? t('packageDetails.session.unlimited') : (pkg.number_of_sessions === -1 || pkg.number_of_sessions === undefined ? t('packageDetails.session.unlimited') : t('packageDetails.session.limit'))}
              </div>
              <div className="text-[11px] text-gray-400 mt-1">{t('packageDetails.session.sessionLabel')}</div>
            </div>
            <div className="p-4 rounded-xl bg-teal-50 border border-teal-100 text-center">
              <div className="flex items-center justify-center">
                {isUnlimited
                  ? <InfinityIcon size={24} className="text-gray-700"/>
                  : <span className="text-lg font-black text-gray-900">{pkg.number_of_sessions ?? '—'}</span>
                }
              </div>
              <div className="text-[11px] text-gray-400 mt-1">{t('packageDetails.session.numberOfSession')}</div>
            </div>
          </div>

          {/* Session name rows */}
          <div className="space-y-2">
            {Array.from({ length: Math.min(sessionDetails.length, Math.ceil(sessionSlotCount)) }).map((_, i) => {
              const detail = sessionDetails[i]
              const enTitle = detail?.translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.title
              const arTitle = detail?.translations?.find((tr:any)=>tr.locale?.toLowerCase()==='ar')?.title
              const urTitle = detail?.translations?.find((tr:any)=>tr.locale?.toLowerCase()==='ur')?.title
              const variants = [
                { code: 'EN', title: enTitle, rtl: false },
                { code: 'AR', title: arTitle, rtl: true },
                { code: 'UR', title: urTitle, rtl: true },
              ].filter(v => v.title)
              return (
                <div key={i} className="flex items-start justify-between gap-3 rounded-xl bg-gray-50/70 border border-gray-100 px-3 py-2.5">
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg bg-teal-500 text-white text-[11px] font-bold mt-0.5">{i+1}</span>
                  <div className="flex flex-1 flex-col items-end gap-1.5 min-w-0">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 self-start">{t('packageDetails.session.sessionName', { index: i+1 })}</span>
                    {variants.length === 0 ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : variants.map(v => (
                      <div key={v.code} className="flex items-center gap-2 max-w-full">
                        <span className="flex-shrink-0 text-[9px] font-bold uppercase text-brand-600 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded">{v.code}</span>
                        <span dir={v.rtl ? 'rtl' : 'ltr'} className="text-xs font-semibold text-gray-900 truncate">{v.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>

      {/* Package Inclusions — session_inclusions per locale */}
      {(() => {
        const incl = (loc: string) => (pkg.package_translations?.find((tr:any)=>tr.locale?.toLowerCase()===loc)?.session_inclusions ?? '')
          .split('\n').map((s:string)=>s.trim()).filter(Boolean)
        const en = incl('en'), ar = incl('ar'), ur = incl('ur')
        if (en.length===0 && ar.length===0 && ur.length===0) return null
        const Col = ({ title, items, rtl }: { title: string; items: string[]; rtl?: boolean }) => items.length===0 ? null : (
          <div className={rtl ? 'text-end' : ''} dir={rtl ? 'rtl' : 'ltr'}>
            <div className="text-xs font-bold text-gray-700 mb-2">{title}</div>
            <ul className="space-y-1.5">
              {items.map((it,i)=>(
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  {!rtl && <CheckSquare size={12} className="text-brand-500 flex-shrink-0 mt-0.5"/>}
                  <span className="flex-1">{it}</span>
                  {rtl && <CheckSquare size={12} className="text-brand-500 flex-shrink-0 mt-0.5"/>}
                </li>
              ))}
            </ul>
          </div>
        )
        return (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-brand-500"><span className="text-sm font-bold text-white">{t('packageDetails.inclusions.title')}</span></div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
              <Col title={t('packageDetails.inclusions.english')} items={en}/>
              <Col title={t('packageDetails.inclusions.arabic')} items={ar} rtl/>
              <Col title={t('packageDetails.inclusions.urdu')} items={ur} rtl/>
            </div>
          </div>
        )
      })()}

      {/* Section edit modals */}
      <EditBasicInfoModal open={editing==='basic'}    onClose={()=>setEditing(null)} pkg={pkg} onSave={onSave} saving={saving}/>
      <EditPricingModal   open={editing==='pricing'}  onClose={()=>setEditing(null)} pkg={pkg} onSave={onSave} saving={saving}/>
      <EditSettingsModal  open={editing==='settings'} onClose={()=>setEditing(null)} pkg={pkg} onSave={onSave} saving={saving}/>
      <EditSessionModal   open={editing==='session'}  onClose={()=>setEditing(null)} pkg={pkg} onSave={onSave} saving={saving}/>
    </div>
  )
}

// ── Shared pastel tile ─────────────────────────────────────────────────────────
function Tile({ value, label, bg }: { value: React.ReactNode; label: string; bg: string }) {
  return (
    <div className={cn('rounded-xl py-3 px-2 text-center', bg)}>
      <div className="text-sm font-black text-gray-900">{value}</div>
      <div className="text-[12px] text-gray-500 mt-0.5">{label}</div>
    </div>
  )
}
function cityName(c: any, i: number) {
  return c.city?.city_translations?.find((t:any)=>t.locale?.toLowerCase()==='en')?.name
    ?? c.city?.city_translations?.[0]?.name ?? c.city_name ?? `City ${i+1}`
}
function money(v: any) { return `AED ${Number(v ?? 0).toLocaleString()}` }
function discountPrice(price: number, type: string, value: number) {
  if (!value) return price
  return type === 'percentage' ? +(price * (1 - value / 100)).toFixed(2) : +(price - value).toFixed(2)
}

// ══════════════════════════════════════════════════════════════════════════════
// Tab: City
// ══════════════════════════════════════════════════════════════════════════════
function CityTab({ pkg, selectMode, selectedCityIds, onToggleCity, onEditCity, canEdit = true }: {
  pkg: any
  selectMode: boolean
  selectedCityIds: number[]
  onToggleCity: (id: number) => void
  onEditCity: (c: any) => void
  canEdit?: boolean
}) {
  const { t } = useTranslation()
  const cities: any[] = pkg.package_city ?? []
  if (cities.length === 0) return (
    <div className="text-center py-16 text-gray-400 text-sm">{t('packageDetails.noCities')}</div>
  )
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      {cities.map((c: any, i: number) => {
        const minPrice = Number(c.min_price ?? c.city_price ?? 0)
        const maxPrice = Number(c.max_price ?? minPrice)
        const price    = minPrice
        const priceDisplay = maxPrice > minPrice ? `AED ${minPrice.toLocaleString()} – ${maxPrice.toLocaleString()}` : money(minPrice)
        const dType    = c.discount_type ?? 'percentage'
        const dValue   = Number(c.discount_value ?? 0)
        const dispType = dType === 'percentage' ? '%' : 'AED'
        // Note: is_published lives on the CITY's own record (c.city.is_published,
        // global to that city), not on this package↔city pricing row — there is
        // no per-package publish state for a city, so this is a read-only signal,
        // never a toggle written from here.
        const published = c.city?.is_published === 1 || c.city?.is_published === true
        const checked  = selectedCityIds.includes(c.id)
        return (
          <div key={c.id ?? i} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-900">{cityName(c, i)}</span>
              {selectMode && canEdit ? (
                <button onClick={()=>onToggleCity(c.id)}
                  className={cn('w-5 h-5 rounded border-2 flex items-center justify-center',
                    checked ? 'bg-brand-500 border-brand-500 text-white' : 'border-gray-300')}>
                  {checked && <span className="text-[13px] font-black">✓</span>}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold',
                    published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                    {published ? t('packageDetails.published') : t('packageDetails.unpublished')}
                  </span>
                  {canEdit && (
                    <button onClick={()=>onEditCity(c)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50">
                      <Edit2 size={11}/> {t('packageDetails.editInfo')}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 grid grid-cols-2 gap-2.5">
              <Tile bg="bg-green-50"  value={priceDisplay} label={t('packageDetails.tiles.cityPrice')}/>
              <Tile bg="bg-sky-50"    value={dValue ? dispType : '--'} label={t('packageDetails.tiles.discountType')}/>
              <Tile bg="bg-sky-50"    value={dValue ? `${dValue}${dType==='percentage'?'%':''}` : '--'} label={t('packageDetails.tiles.discountValue')}/>
              <Tile bg="bg-brand-50"  value={c.offer_valid_until ? fmtDate(c.offer_valid_until) : '--'} label={t('packageDetails.tiles.discountValidUntil')}/>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Tab: School
// ══════════════════════════════════════════════════════════════════════════════
function SchoolTab({ pkg }: { pkg: any }) {
  const { t } = useTranslation()
  // package_vendor[] holds per-school pricing; fall back to the single vendor
  const schools: any[] = pkg.package_vendor ?? pkg.package_vendors ?? (pkg.vendor ? [{ vendor: pkg.vendor, ...pkg }] : [])
  if (schools.length === 0) return <div className="text-center py-16 text-gray-400 text-sm">{t('packageDetails.noSchools')}</div>

  const schoolName = (s: any, i: number) => {
    const v = s.vendor ?? s
    return v.vendor_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.name ?? v.vendor_translations?.[0]?.name ?? s.school_name ?? t('packageDetails.defaultSchoolName', { index: i+1 })
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      {schools.map((s: any, i: number) => {
        const price    = Number(s.price ?? s.min_price ?? pkg.min_price ?? 0)
        const dType    = s.discount_type ?? 'percentage'
        const dValue   = Number(s.discount_value ?? 0)
        const dispType = dType === 'percentage' ? '%' : 'AED'
        return (
          <div key={s.id ?? i} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-900">{schoolName(s, i)}</span>
            </div>
            <div className="p-4 grid grid-cols-3 gap-2.5">
              <Tile bg="bg-green-50" value={money(price)} label={t('packageDetails.tiles.price')}/>
              <Tile bg="bg-sky-50"   value={dValue ? dispType : '--'} label={t('packageDetails.tiles.discountType')}/>
              <Tile bg="bg-sky-50"   value={dValue ? `${dValue}${dType==='percentage'?'%':''}` : '--'} label={t('packageDetails.tiles.discountValue')}/>
              <Tile bg="bg-teal-50"  value={dValue ? `AED ${discountPrice(price, dType, dValue)}` : 'AED --'} label={t('packageDetails.tiles.discountPrice')}/>
              <Tile bg="bg-teal-50"  value={s.offer_valid_from ? fmtDate(s.offer_valid_from) : '--'} label={t('packageDetails.tiles.startDate')}/>
              <Tile bg="bg-brand-50" value={s.offer_valid_until ? fmtDate(s.offer_valid_until) : '--'} label={t('packageDetails.tiles.discountValidUntil')}/>
            </div>
            {(() => {
              const v = s.vendor ?? s
              if (!v?.email && !v?.phone_number && !v?.status) return null
              return (
                <div className="px-4 pb-4 pt-1 space-y-0">
                  <Info label={t('packageDetails.schoolInfo.email')}   value={v.email}/>
                  <Info label={t('packageDetails.schoolInfo.phone')}   value={v.phone_number}/>
                  <Info label={t('packageDetails.schoolInfo.status')}  value={v.status ? <span className="capitalize">{v.status}</span> : '—'}/>
                  <Info label={t('packageDetails.schoolInfo.schoolCommission')}      value={v.min_commision!=null||v.max_commision!=null ? `${v.min_commision ?? 0}% – ${v.max_commision ?? 0}%` : '—'}/>
                  <Info label={t('packageDetails.schoolInfo.certificateCommission')} value={v.certificate_commission_in_percentage!=null ? `${v.certificate_commission_in_percentage}%` : '—'}/>
                </div>
              )
            })()}
          </div>
        )
      })}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function PackageDetailsPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isSchool = location.pathname.startsWith('/school')
  const qc = useQueryClient()
  const [editMode, setEditMode] = useState(false)
  // city edit
  const [citySelect, setCitySelect] = useState(false)
  const [selectedCityIds, setSelectedCityIds] = useState<number[]>([])
  const [cityModalOpen, setCityModalOpen] = useState(false)
  const [cityTargets, setCityTargets] = useState<any[]>([])
  // add city
  const [addCityOpen, setAddCityOpen] = useState(false)
  const [addCityPicked, setAddCityPicked] = useState<{ id: number; name: string } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['package-detail', id],
    queryFn: () => packageService.getById(Number(id)),
    enabled: !!id,
  })

  // Master lists for "Add City" / "Add School" pickers.
  const { data: allCitiesData }   = useQuery({ queryKey:['cities-pkg'],  queryFn:()=>restClient.get(`${endpoints.city.list}?limit=1000&locale=en`).then(r=>r.data), staleTime: 60_000 })
  const allCities: any[]  = allCitiesData?.data  ?? []
  const getCityOptionName = (c: any) => c.city_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.name ?? c.city_translations?.[0]?.name ?? t('packageDetails.defaultCityName', { index: c.id })

  const pkg = (data as any)?.data ?? data ?? {}
  const cities: any[] = pkg.package_city ?? []
  const refetchPkg = () => qc.invalidateQueries({ queryKey: ['package-detail', id] })
  const cityIdOf = (c: any) => c.city_id ?? c.city?.id ?? c.id

  const attachedCityIds = new Set(cities.map(cityIdOf))
  const availableCities = allCities.filter((c:any) => !attachedCityIds.has(c.id))

  const toggleCity = (rowId: number) =>
    setSelectedCityIds(p => p.includes(rowId) ? p.filter(x => x !== rowId) : [...p, rowId])
  const exitCitySelect = () => { setCitySelect(false); setSelectedCityIds([]) }
  const openSingleCity = (c: any) => { setCityTargets([c]); setCityModalOpen(true) }
  const openMultiCity = () => {
    setCityTargets(cities.filter(c => selectedCityIds.includes(c.id)))
    setCityModalOpen(true)
  }
  const cityRefs = cityTargets.map((c, i) => ({ id: cityIdOf(c), name: cityName(c, i), packageCityId: c.id }))
  const name = pkg.package_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.name ?? '—'
  const isPublished = pkg.is_published === 1

  // Save one section: resend the FULL package (rebuilt unchanged from the loaded
  // detail) with a small `patch` applied on top, so only the edited section is
  // modified. Returns true on success.
  const [saving, setSaving] = useState(false)
  const savePackageSection = async (patch: Record<string, any>, okKey: string) => {
    if (saving) return false
    setSaving(true)
    try {
      await restClient.post(endpoints.package.createUpdate, { ...buildPackagePayload(pkg), ...patch })
      await refetchPkg()
      toast.success(t(okKey))
      return true
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('packageDetails.saveFailed'))
      return false
    } finally {
      setSaving(false)
    }
  }

  const [publishing, setPublishing] = useState(false)
  const togglePublishPackage = async () => {
    if (publishing) return
    const next: 0 | 1 = isPublished ? 0 : 1
    setPublishing(true)
    try {
      await restClient.post(endpoints.package.createUpdate, { ...buildPackagePayload(pkg), is_published: next })
      await refetchPkg()
      toast.success(next ? t('packageDetails.publishedToast') : t('packageDetails.unpublishedToast'))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('packageDetails.publishFailed'))
    } finally {
      setPublishing(false)
    }
  }

  if (isLoading) return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 bg-gray-200 rounded w-48"/>
      <div className="h-12 bg-gray-200 rounded w-full"/>
      <div className="grid grid-cols-2 gap-4"><div className="h-64 bg-gray-200 rounded-2xl"/><div className="h-64 bg-gray-200 rounded-2xl"/></div>
    </div>
  )

  if (isError) return (
    <div className="text-center py-20">
      <p className="text-sm text-red-500 font-semibold">{t('packageDetails.failedToLoad')}</p>
      <button onClick={()=>navigate(-1)} className="mt-3 text-xs text-brand-600 hover:underline flex items-center gap-1 mx-auto"><ChevronLeft size={12} className="rtl:rotate-180"/>{t('packageDetails.goBack')}</button>
    </div>
  )

  if (editMode) return <PackageCreatePage editPackage={pkg}/>

  return (
    <div className="space-y-5">

      {/* ── Breadcrumb ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span className="cursor-pointer hover:text-brand-500" onClick={()=>navigate(isSchool ? '/school/package-list' : '/dashboard/package/list')}>{t('packageDetails.breadcrumb.dashboard')}</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-brand-500" onClick={()=>navigate(isSchool ? '/school/package-list' : '/dashboard/package/list')}>{t('packageDetails.breadcrumb.packageManagement')}</span>
            <span>/</span>
            <span className="text-brand-500 font-semibold">{t('packageDetails.breadcrumb.packageDetails')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('packageDetails.title')}</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Publish toggle */}
          <button
            type="button"
            onClick={togglePublishPackage}
            disabled={publishing}
            title={t('packageDetails.publishUnpublish')}
            className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors disabled:opacity-60',
              isPublished ? 'bg-brand-500 text-white border-brand-500 hover:bg-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}>
            <span className={cn('relative inline-flex w-8 h-4 rounded-full transition-colors flex-shrink-0', isPublished ? 'bg-white/30' : 'bg-gray-300')}>
              <span className={cn('absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all', isPublished ? 'start-4' : 'start-0.5')}/>
            </span>
            {isPublished ? t('packageDetails.published') : t('packageDetails.unpublished')}
          </button>

          {!isSchool && (
            <button onClick={()=>setEditMode(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50 transition-colors">
              <Edit2 size={12}/> {t('packageDetails.editPackage')}
            </button>
          )}
        </div>
      </div>

      {/* ── Package Details ── */}
      <DetailsTab pkg={pkg} onSave={savePackageSection} saving={saving} canEdit={!isSchool}/>

      {/* ── City ── */}
      <SectionHeading>{t('packageDetails.tabs.city')}</SectionHeading>
      {!isSchool && (
        <div className="flex items-center justify-between gap-2 -mt-2 flex-wrap">
          <select
            value=""
            onChange={e => {
              const cid = Number(e.target.value)
              const c = availableCities.find((x:any)=>x.id===cid)
              if (c) { setAddCityPicked({ id: c.id, name: getCityOptionName(c) }); setAddCityOpen(true) }
            }}
            disabled={availableCities.length === 0}
            className="px-3 py-1.5 rounded-lg border border-dashed border-brand-300 text-brand-600 text-xs font-semibold bg-white hover:bg-brand-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <option value="">+ {t('packageDetails.addCity')}</option>
            {availableCities.map((c:any)=><option key={c.id} value={c.id}>{getCityOptionName(c)}</option>)}
          </select>
          {citySelect ? (
            <div className="flex items-center gap-2">
              <button onClick={openMultiCity} disabled={selectedCityIds.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors disabled:opacity-50">
                <Edit2 size={12}/> {t('packageDetails.editConfirmSelected', { count: selectedCityIds.length })}
              </button>
              <button onClick={exitCitySelect}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-gray-50 transition-colors">
                <X size={12}/> {t('packageDetails.cancel')}
              </button>
            </div>
          ) : (
            <button onClick={()=>setCitySelect(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-300 text-brand-600 text-xs font-semibold hover:bg-brand-50 transition-colors">
              <CheckSquare size={12}/> {t('packageDetails.editMultipleCity')}
            </button>
          )}
        </div>
      )}
      <CityTab pkg={pkg}
        canEdit={!isSchool}
        selectMode={citySelect}
        selectedCityIds={selectedCityIds}
        onToggleCity={toggleCity}
        onEditCity={openSingleCity}/>

      {/* ── School ── */}
      <SectionHeading>{t('packageDetails.tabs.school')}</SectionHeading>
      <SchoolTab pkg={pkg}/>

      {/* ── Edit City Info modal ── */}
      <EditCityInfoModal
        open={cityModalOpen}
        onClose={()=>setCityModalOpen(false)}
        packageId={pkg.id}
        cities={cityRefs}
        initial={cityTargets.length === 1 ? cityTargets[0] : undefined}
        onSuccess={()=>{ refetchPkg(); exitCitySelect() }}
      />

      {/* ── Add City modal (new city, blank pricing form) ── */}
      <EditCityInfoModal
        open={addCityOpen}
        onClose={()=>{ setAddCityOpen(false); setAddCityPicked(null) }}
        packageId={pkg.id}
        cities={addCityPicked ? [addCityPicked] : []}
        onSuccess={()=>{ refetchPkg(); setAddCityOpen(false); setAddCityPicked(null); toast.success(t('packageDetails.cityAdded')) }}
      />
    </div>
  )
}
